import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2, Lock, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabaseUrl, supabaseAnonKey } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generalDonorSchema, type GeneralDonorFormData } from "./donorSchema";

const PRESET_AMOUNTS = [25, 50, 100, 250, 500, 1000] as const;

type DonationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const darkFieldClass =
  "bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-phcsn-gold focus-visible:ring-phcsn-gold/30";

const DonationModal = ({ open, onOpenChange }: DonationModalProps) => {
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<GeneralDonorFormData>({
    resolver: zodResolver(generalDonorSchema),
    defaultValues: {
      fullName: "",
      email: "",
      organization: "",
    },
  });

  const effectiveAmount = selectedPreset ?? (customAmount ? Number(customAmount) : null);
  const isValidAmount =
    effectiveAmount !== null &&
    effectiveAmount >= 1 &&
    effectiveAmount <= 100000 &&
    Number.isFinite(effectiveAmount);

  const handlePresetClick = useCallback((amount: number) => {
    if (selectedPreset === amount) {
      setSelectedPreset(null);
      setCustomAmount("");
    } else {
      setSelectedPreset(amount);
      setCustomAmount(String(amount));
    }
  }, [selectedPreset]);

  const handleCustomChange = useCallback((value: string) => {
    setCustomAmount(value);
    const num = Number(value);
    if (PRESET_AMOUNTS.includes(num as (typeof PRESET_AMOUNTS)[number])) {
      setSelectedPreset(num);
    } else {
      setSelectedPreset(null);
    }
  }, []);

  const handleDonate = async (donor: GeneralDonorFormData) => {
    if (!isValidAmount || !effectiveAmount) return;

    setIsLoading(true);
    try {
      const checkoutEndpoint = `${supabaseUrl}/functions/v1/create-checkout`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${supabaseAnonKey}`,
        apikey: supabaseAnonKey,
      };
      // Stripe-side composition expects firstName/lastName; split fullName on first space.
      // Donors typing "Aaron C. Dickason Sr." will land as first="Aaron", last="C. Dickason Sr."
      // — adequate for general donations where structured names aren't required.
      const trimmedName = donor.fullName.trim();
      const spaceIdx = trimmedName.indexOf(" ");
      const firstName = spaceIdx === -1 ? trimmedName : trimmedName.slice(0, spaceIdx);
      const lastName = spaceIdx === -1 ? "" : trimmedName.slice(spaceIdx + 1);

      const payload = JSON.stringify({
        amount: Math.round(effectiveAmount * 100),
        campaign: "general",
        donor: {
          firstName,
          lastName,
          email: donor.email,
          organization: donor.organization,
        },
      });

      let response: Response | null = null;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          response = await fetch(checkoutEndpoint, {
            method: "POST",
            headers,
            body: payload,
          });
          if (response.ok) break;
          if (response.status >= 500 && attempt === 0) {
            await new Promise((r) => setTimeout(r, 1500));
            continue;
          }
          break;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
      }

      if (!response) {
        throw new Error(
          lastError?.message || "Network error — please check your connection."
        );
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        console.error("Checkout HTTP error:", response.status, text);
        throw new Error(
          `Server error (${response.status}). Please try again.`
        );
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const text = await response.text().catch(() => "");
        console.error("Non-JSON response:", contentType, text.slice(0, 200));
        throw new Error("Unexpected response from server. Please try again.");
      }

      const data = await response.json();
      if (!data?.url) {
        console.error("No checkout URL in response:", data);
        throw new Error("No checkout URL returned");
      }

      window.location.href = data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      const message =
        err instanceof Error
          ? err.message
          : "Please try again or contact support if the issue persists.";
      toast({
        title: "Unable to start checkout",
        description: message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedPreset(null);
      setCustomAmount("");
      setIsLoading(false);
      form.reset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-phcsn-navy border-white/10 text-white sm:max-w-md max-h-[90vh] overflow-y-auto [&>button]:text-white [&>button]:hover:text-phcsn-gold">
        <DialogHeader>
          <DialogTitle className="text-2xl font-playfair text-white flex items-center gap-2">
            <Heart className="w-6 h-6 text-phcsn-gold" />
            Support Our Mission
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Your gift helps PHCSN strengthen communities across Michigan through
            scholarships, service, and youth development.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleDonate)}
            className="space-y-5 pt-2"
          >
            {/* Donor Info */}
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/70 text-sm">
                      Full Name *
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your name"
                        className={darkFieldClass}
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/70 text-sm">
                      Email *
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        className={darkFieldClass}
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organization"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/70 text-sm">
                      Organization{" "}
                      <span className="text-white/40 text-xs">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Lodge, business, foundation, etc."
                        className={darkFieldClass}
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Preset tier grid */}
            <div className="grid grid-cols-3 gap-3">
              {PRESET_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  disabled={isLoading}
                  onClick={() => handlePresetClick(amount)}
                  className={cn(
                    "py-3 px-2 rounded-lg border-2 text-lg font-semibold transition-all duration-200",
                    "hover:border-phcsn-gold/60 hover:bg-phcsn-gold/10",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-phcsn-gold",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    selectedPreset === amount
                      ? "border-phcsn-gold bg-phcsn-gold/15 text-phcsn-gold"
                      : "border-white/20 text-white/90 bg-white/5"
                  )}
                >
                  ${amount.toLocaleString()}
                </button>
              ))}
            </div>

            {/* Custom amount input */}
            <div className="space-y-2">
              <label
                htmlFor="custom-amount"
                className="text-sm text-white/60 font-medium"
              >
                Or enter a custom amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-lg font-semibold">
                  $
                </span>
                <Input
                  id="custom-amount"
                  type="number"
                  min={1}
                  max={100000}
                  step={1}
                  placeholder="Other amount"
                  value={customAmount}
                  onChange={(e) => handleCustomChange(e.target.value)}
                  disabled={isLoading}
                  className="pl-8 bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-phcsn-gold focus:ring-phcsn-gold"
                />
              </div>
            </div>

            {/* Donate button */}
            <Button
              type="submit"
              variant="gold"
              className="w-full text-lg py-6 font-semibold shadow-lg hover:shadow-xl"
              disabled={!isValidAmount || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : isValidAmount ? (
                `Donate $${effectiveAmount!.toLocaleString()}`
              ) : (
                "Select an amount"
              )}
            </Button>

            {/* Trust footer */}
            <div className="flex flex-col items-center gap-1 pt-1">
              <p className="text-xs text-white/40 flex items-center gap-1.5">
                <Lock className="w-3 h-3" />
                Secure payment powered by Stripe
              </p>
              <p className="text-xs text-white/40">
                Tax-deductible 501(c)(3) donation
              </p>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DonationModal;
